import { useState, useEffect, useCallback } from 'react';

import './Style.css';
import ModalTrigger from '../../../shared/modal/ModalTrigger';

type IconSelectProps = {
  questionTitle: string;
  questionTitleSuffix?: string;
  modalText?: string;
  onSetValue: (value: { id: string; value: string }) => void;
  onOpenModal?: () => void;
  questionid: string;
  assessmentValues: Record<string, unknown>;
  width: string;
  range: string;
  labelText: string;
};

export function IconSelect({
  questionTitle,
  questionTitleSuffix,
  modalText,
  onSetValue,
  onOpenModal,
  questionid,
  assessmentValues,
  width,
  range,
  labelText,
}: IconSelectProps) {
  const [radioOption, setRadioOption] = useState<string | null>(null);

  const updateIconsActive = useCallback(
    (value: string) => {
      if (!value) {
        // Clear all selections
        for (let i = 0; i <= parseFloat(range); i++) {
          const el = document.getElementById(questionid + i);
          if (el) el.classList.remove('selected');
        }
        return;
      }

      const valueNum = parseInt(value, 10);
      for (let i = 0; i <= parseFloat(range); i++) {
        const el = document.getElementById(questionid + i);
        if (el) {
          if (i <= valueNum) {
            el.classList.add('selected');
          } else {
            el.classList.remove('selected');
          }
        }
      }
    },
    [questionid, range]
  );

  useEffect(() => {
    const value = assessmentValues[questionid];
    if (value && radioOption !== String(value)) {
      const stringValue = String(value);
      setRadioOption(stringValue);
      updateIconsActive(stringValue);
    }
  }, [assessmentValues, questionid, radioOption, updateIconsActive]);

  function setIconSelectHandler(event: React.ChangeEvent<HTMLInputElement>) {
    const { value, name } = event.target;
    if (!value || !name) return;

    if (value === radioOption) {
      // double click deselect (custom logic)
      setRadioOption(null);
      updateIconsActive('');
      onSetValue({ id: name, value: '' });
    } else {
      setRadioOption(value);
      updateIconsActive(value);
      onSetValue({ id: name, value });
    }
  }

  const rows = [];
  for (let i = 0; i <= parseFloat(range); i++) {
    const k = i;
    let suffix = '';
    if (k === parseFloat(range)) {
      suffix = '+';
    }
    rows.push({ index: i, value: k, suffix });
  }

  return (
    <div className={`req question_block${width}`}>
      <fieldset id={questionid} data-input-type="icon-select">
        <legend className="assessment_question_title">{questionTitle}</legend>

        {questionTitleSuffix ? <span className="titleSuffix">{questionTitleSuffix}</span> : null}
        {modalText && (
          <ModalTrigger
            modalText={modalText}
            onOpenModal={onOpenModal}
            question={questionid}
            questionTitle={questionTitle}
          />
        )}
        <div className="answer_block fs-exclude">
          <div
            className={`dot_radio iconselect ${questionid}`}
            id={`${questionid}-icon-select-container`}
          >
            {rows.map((option) => {
              return (
                <div className="input_wrap" key={option.index}>
                  <input
                    type="radio"
                    id={questionid + option.value}
                    name={questionid}
                    value={String(option.value)}
                    onChange={setIconSelectHandler}
                    checked={String(radioOption) === String(option.value)}
                    aria-labelledby={`${questionid}_${option.value}_label`}
                  />
                  <label
                    htmlFor={questionid + option.value}
                    aria-hidden="true"
                    id={`${questionid}_${option.value}_label`}
                  >
                    <span className="visuallyHidden">
                      {`${option.value} ${option.suffix} ${labelText}`}
                    </span>
                  </label>
                  <div aria-hidden="true" className="icon_label">
                    {option.value}
                    {option.suffix}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </fieldset>
    </div>
  );
}
